import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAccountManager } from '@/hooks/use-account-manager';
import { useSessionClaims } from '@/hooks/use-session-claims';
import { MANAGE_NAV } from '@/config/manage-nav';
import { MANAGE_ROUTES } from '@/config/router';

/**
 * Staff operations area.
 *
 * Not org-scoped: everything here configures things shared by every merchant —
 * a single Apple Pay platform integrator, the shared certificate pool — so
 * scoping it to an organization would imply per-org configuration that does not
 * exist.
 *
 * The role is enforced once here rather than in each page, so a new tool cannot
 * ship without a gate by forgetting to add one.
 */
export default function ManageLayout() {
  const { t } = useTranslation('navigation');
  const { pathname } = useLocation();
  const { isAccountManager, isLoading } = useAccountManager();
  const { isSuperAdmin, isPartnerAdmin, whitelabelScope } = useSessionClaims();

  // Tagada staff (no partner scope) see every tool; partner admins only the
  // ones marked for them, since most of these administer things across every
  // merchant and partner.
  const isStaff = (isAccountManager || isSuperAdmin) && !whitelabelScope;
  const visibleNav = MANAGE_NAV.filter((item) => isStaff || (isPartnerAdmin && item.allowPartnerAdmin));

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
      </div>
    );
  }

  // A tool this role cannot see must also refuse to render when typed
  // directly, or hiding the tab would be decoration rather than a gate.
  const isOnVisibleTool = visibleNav.some(
    ({ to }) => pathname === to || pathname.startsWith(`${to}/`),
  );

  if (visibleNav.length === 0 || (pathname !== MANAGE_ROUTES.BASE && !isOnVisibleTool)) {
    return (
      <div className="flex h-96 flex-col items-center justify-center gap-2">
        <h2 className="text-xl font-semibold">{t('manageAccessDeniedTitle')}</h2>
        <p className="text-muted-foreground">{t('manageAccessDeniedDescription')}</p>
      </div>
    );
  }

  // Land on the first tool this role can open, not a hardcoded one — the
  // area's first tab is not necessarily visible to everybody.
  if (pathname === MANAGE_ROUTES.BASE) {
    return <Navigate to={visibleNav[0].to} replace />;
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="mb-6 space-y-1">
        <h1 className="text-2xl font-semibold">{t('manage')}</h1>
        <p className="text-muted-foreground text-sm">{t('manageDescription')}</p>
      </div>

      <nav className="border-border mb-6 flex gap-1 overflow-x-auto border-b">
        {visibleNav.map(({ to, labelKey, icon: Icon }) => {
          const isActive = pathname === to || pathname.startsWith(`${to}/`);
          return (
            <Link
              key={to}
              to={to}
              className={cn(
                'flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm transition-colors',
                isActive
                  ? 'border-primary text-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground border-transparent',
              )}
            >
              <Icon className="h-4 w-4" />
              {t(labelKey)}
            </Link>
          );
        })}
      </nav>

      <Outlet />
    </div>
  );
}
